import { useQuery } from '@apollo/client/react';
import { Ticket, Mountain, Trophy, Shield, PlusCircle, Medal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Page } from '@/components/layout/Page';
import { PageContent } from '@/components/layout/PageContent';
import { LeaderboardWidget } from '@/components/home/LeaderboardWidget';
import { MyBetsPreview } from '@/components/home/MyBetsPreview';
import { OpenRaces } from '@/components/home/OpenRaces';
import { QuickActions } from '@/components/home/QuickActions';
import { HomePageSkeleton } from '@/components/skeletons/HomePageSkeleton';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { useAuth } from '@/context/AuthContext';
import { HOME_STATS, HOME_OPEN_RACES, HOME_MY_BETS, HOME_LEADERBOARD } from '@/graphql/queries';
import { RaceStatus, SortOrder, RaceSortField, BetSortField, Role } from '@/gql/graphql';
import { formatScore } from '@/lib/utils';

export function HomePage() {
    const { t } = useTranslation('home');
    const { t: tc } = useTranslation();
    const { user: authUser } = useAuth();

    const { data: statsData, loading: statsLoading, error: statsError } = useQuery(HOME_STATS);

    const { data: racesData, loading: racesLoading } = useQuery(HOME_OPEN_RACES, {
        variables: {
            pagination: { page: 1, limit: 6 },
            filter: { status: RaceStatus.Open },
            sort: { field: RaceSortField.BettingDeadline, order: SortOrder.Asc },
        },
    });

    const { data: betsData, loading: betsLoading } = useQuery(HOME_MY_BETS, {
        variables: {
            pagination: { page: 1, limit: 6 },
            filter: { raceStatus: RaceStatus.Open },
            sort: { field: BetSortField.UpdatedAt, order: SortOrder.Desc },
        },
    });

    const { data: leaderboardData, loading: leaderboardLoading } = useQuery(HOME_LEADERBOARD, {
        variables: {
            pagination: { page: 1, limit: 3 },
        },
    });

    const isInitialLoading = statsLoading && racesLoading && betsLoading && leaderboardLoading;

    if (isInitialLoading) return <HomePageSkeleton />;
    if (statsError) return <div className="p-4"><ErrorBanner message={tc('error.generic')} /></div>;

    const user = statsData?.me;
    const stats = user?.stats;
    const races = racesData?.listRaces?.items ?? [];
    const bets = betsData?.myBets?.items ?? [];
    const leaderboard = leaderboardData?.globalLeaderboard;
    const isAdmin = authUser?.roles?.includes(Role.Admin);
    const hasCreatedRaces = (stats?.racesCreated ?? 0) > 0;

    return (
        <Page className="bg-slate-50">
            <PageContent width="wide" className="space-y-6 md:space-y-8 pb-24 md:pb-6">
                {/* --- HERO: Greeting + Stats (full width) --- */}
                <div className="animate-section animate-section-1 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-3xl p-6 md:p-8 text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />

                    <div className="relative">
                        <div className="flex items-center gap-3 mb-1">
                            <h1 className="text-2xl md:text-3xl font-black">
                                {t(`greeting.${getGreetingKey()}`, { username: user?.username })}
                            </h1>
                            {isAdmin && (
                                <div className="bg-amber-400/20 text-amber-200 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide flex items-center gap-1">
                                    <Shield size={12} />
                                    Admin
                                </div>
                            )}
                            {!isAdmin && hasCreatedRaces && (
                                <div className="bg-white/15 text-white/80 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide flex items-center gap-1">
                                    <PlusCircle size={12} />
                                    {t('badge.creator')}
                                </div>
                            )}
                        </div>
                        <p className="text-emerald-100 text-sm md:text-base mb-6">
                            {t('subtitle')}
                        </p>

                        {stats && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                                <StatCard
                                    icon={<Ticket size={18} />}
                                    label={t('stats.totalBets')}
                                    value={stats.totalBets ?? 0}
                                />
                                <StatCard
                                    icon={<Mountain size={18} />}
                                    label={t('stats.racesJoined')}
                                    value={stats.totalRaces ?? 0}
                                />
                                <StatCard
                                    icon={<Trophy size={18} />}
                                    label={t('stats.totalScore')}
                                    value={formatScore(stats.totalScore ?? 0)}
                                />
                                <StatCard
                                    icon={<Medal size={18} />}
                                    label={t('stats.globalRank')}
                                    value={leaderboard?.currentUserRank
                                        ? t('stats.rankValue', { rank: leaderboard.currentUserRank, total: leaderboard.totalCount })
                                        : '-'
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* --- 2-COLUMN LAYOUT on desktop --- */}
                <div className="flex flex-col lg:flex-row lg:gap-8">
                    {/* Main column */}
                    <div className="flex-1 min-w-0 space-y-6 md:space-y-8">
                        <div className="animate-section animate-section-2">
                            <OpenRaces races={races} totalCount={racesData?.listRaces?.totalCount ?? 0} />
                        </div>

                        <div className="animate-section animate-section-3">
                            <MyBetsPreview bets={bets} />
                        </div>
                    </div>

                    {/* Sidebar (stacks below on mobile) */}
                    <div className="lg:w-80 xl:w-96 space-y-6 md:space-y-8 mt-6 md:mt-8 lg:mt-0 flex-shrink-0">
                        <div className="animate-section animate-section-4 order-first lg:order-none">
                            <QuickActions />
                        </div>

                        <div className="animate-section animate-section-5">
                            {leaderboard && <LeaderboardWidget leaderboard={leaderboard} />}
                        </div>
                    </div>
                </div>
            </PageContent>
        </Page>
    );
}

// --- Helpers ---

function getGreetingKey(): 'morning' | 'afternoon' | 'evening' {
    const hour = new Date().getHours();
    if (hour < 12) return 'morning';
    if (hour < 18) return 'afternoon';
    return 'evening';
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
    return (
        <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-3 md:p-4">
            <div className="flex items-center gap-2 text-emerald-100 mb-1">
                {icon}
                <span className="text-xs font-bold uppercase tracking-wider">{label}</span>
            </div>
            <div className="text-xl md:text-2xl font-black">{value}</div>
        </div>
    );
}
